import { Injectable } from '@angular/core';

const TOKEN_KEY = 's5_access_token';
// Refresh/deny a bit before actual expiry so an about-to-die token isn't used.
const EXPIRY_SKEW_MS = 30_000;
// JS-readable twin of the HttpOnly s5_session cookie — set by api-server at
// login so the SPA can cheaply tell whether a shared-domain session might
// exist before paying for a /api/auth/local/session round-trip.
const SESSION_HINT_COOKIE = 's5_session_hint';

/**
 * Self-hosted authentication session store (#4960 phase 3, #16114).
 *
 * Holds the SlapFive-issued JWT minted by POST /api/auth/local/login or the
 * set-password endpoints. AuthInitializerService.getToken() prefers this over
 * the Auth0 SDK when present; phase 6 (#16117) replaces the precedence check
 * with a proper provider-aware driver split.
 */
@Injectable({ providedIn: 'root' })
export class SelfHostedAuthService {

  /**
   * Deliberately no HttpClient: this service is constructed inside the
   * HTTP_INTERCEPTORS chain (AuthTokenInterceptor -> AuthInitializerService
   * -> SelfHostedAuthService), so injecting HttpClient re-enters the token
   * while it is still resolving (NG0200) on origins with no stored JWT.
   * The session endpoints are cookie-authenticated same-origin POSTs, so
   * plain fetch is both sufficient and correctly bypasses bearer handling.
   */
  constructor() {}

  /** Returns the stored token if present and not expired/expiring. */
  getToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      return null;
    }
    const exp = this.tokenExpiryMs(token);
    if (exp !== null && exp - EXPIRY_SKEW_MS <= Date.now()) {
      this.clear();
      return null;
    }
    return token;
  }

  /**
   * True when a self-hosted session exists — either a live stored JWT or the
   * shared-domain session cookie (hint marker) that can mint one on demand.
   * The hint alone counts because canActivate/handleLogin route through
   * restoreSession() when the local token is missing or expired, and logout
   * must still revoke the server-side UserSession even after the JWT lapses.
   */
  hasSession(): boolean {
    return this.getToken() !== null || this.hasSessionHint();
  }

  setSession(accessToken: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
  }

  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
  }

  /** Decode the JWT payload claims (unverified — display/authorization-hint only). */
  getClaims(): Record<string, any> | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }
    try {
      const payload = token.split('.')[1];
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(normalized));
    } catch {
      return null;
    }
  }

  /**
   * Attempt to resume a session via the shared-domain s5_session cookie
   * (#4960 phase 4). On success the minted tenant JWT is stored and returned;
   * returns null when there is no cookie hint or the session is gone. The
   * hint cookie keeps this a no-op for anonymous visitors and Auth0 tenants.
   */
  async restoreSession(): Promise<string | null> {
    const existing = this.getToken();
    if (existing) {
      return existing;
    }
    if (!this.hasSessionHint()) {
      return null;
    }
    try {
      const res = await fetch('/api/auth/local/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: '{}',
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.accessToken) {
          this.setSession(data.accessToken);
          return data.accessToken;
        }
      }
      // 401 clears the session cookies server-side, retiring the hint.
    } catch {
      // No valid shared session — caller falls back to interactive login.
    }
    return null;
  }

  /** Revoke the server-side UserSession and clear local session state. */
  async logout(): Promise<void> {
    try {
      await fetch('/api/auth/local/logout', {
        method: 'POST',
        credentials: 'same-origin',
      });
    } catch {
      // Best-effort — local state is cleared regardless.
    }
    this.clear();
  }

  private hasSessionHint(): boolean {
    return typeof document !== 'undefined' &&
      document.cookie.split(';').some(c => c.trim() === `${SESSION_HINT_COOKIE}=1`);
  }

  private tokenExpiryMs(token: string): number | null {
    const claims = (() => {
      try {
        const payload = token.split('.')[1];
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(normalized));
      } catch {
        return null;
      }
    })();
    return typeof claims?.exp === 'number' ? claims.exp * 1000 : null;
  }
}
